import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import { toast } from 'sonner';
import { PagoProveedorSelector, pagoIncompleto, resolverCondicionInicial } from './comparativo/PagoProveedorSelector';
import {
  Loader2, ArrowLeft, CheckCircle, Check, BarChart3, ShoppingCart,
  Plus, UserPlus, X, Save, Printer, PackagePlus, Pencil, Search, AlertTriangle, Ban, Trash2
} from 'lucide-react';

function formatCLP(n) {
  if (!n && n !== 0) return '-';
  return '$' + Math.round(n).toLocaleString('es-CL');
}

export default function ComparativoCotizacion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [generando, setGenerando] = useState(false);
  const [ocsCreadas, setOcsCreadas] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [printing, setPrinting] = useState(false);
  // Iter 286.d — 13 condiciones vigentes (CP-*)
  const [condicionesVigentes, setCondicionesVigentes] = useState([]);
  // Iter 374 — medios de pago + datos de pago elegidos por proveedor { [provIdx]: {condicion_pago_codigo, medio_pago_codigo} }
  const [mediosPago, setMediosPago] = useState([]);
  const [pagos, setPagos] = useState({});

  // Manual provider state
  const [showAddProv, setShowAddProv] = useState(false);
  const [manualProv, setManualProv] = useState({ nombre: '', rut: '', email: '', condicion_pago: '', plazo_entrega: '', moneda: 'CLP' });
  const [manualPrecios, setManualPrecios] = useState({});
  const [savingManual, setSavingManual] = useState(false);
  // Iter 313.g — TC auto-cargado para el proveedor manual (si moneda != CLP)
  const [manualTC, setManualTC] = useState(null);
  const [manualTCFecha, setManualTCFecha] = useState('');
  // Provider search state
  const [provSearch, setProvSearch] = useState('');
  const [provResults, setProvResults] = useState([]);
  const [provSearching, setProvSearching] = useState(false);
  const provDebRef = useRef(null);

  // Additional item state
  const [showAddItem, setShowAddItem] = useState(false);
  const [addItem, setAddItem] = useState({ descripcion: '', cantidad: '1', unidad: 'UN', codigo: '', observacion: '' });
  const [addItemPrecios, setAddItemPrecios] = useState({});
  const [savingItem, setSavingItem] = useState(false);
  const [matSearch, setMatSearch] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [matSearching, setMatSearching] = useState(false);
  const matDebRef = useRef(null);

  // Inline cell price editing (items adicionales)
  const [editCell, setEditCell] = useState(null); // {itemIdx, provIdx}
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);

  const searchProviders = (query) => {
    if (provDebRef.current) clearTimeout(provDebRef.current);
    setProvSearch(query);
    if (query.length < 2) { setProvResults([]); return; }
    provDebRef.current = setTimeout(async () => {
      setProvSearching(true);
      try {
        const res = await api.get(`/compras/cotizaciones/proveedores/buscar?q=${encodeURIComponent(query)}`);
        setProvResults(res.data?.proveedores || []);
      } catch { setProvResults([]); }
      setProvSearching(false);
    }, 300);
  };

  const searchMateriales = (query) => {
    if (matDebRef.current) clearTimeout(matDebRef.current);
    setMatSearch(query);
    if (query.length < 2) { setMatResults([]); return; }
    matDebRef.current = setTimeout(async () => {
      setMatSearching(true);
      try {
        const res = await api.get(`/compras/materiales?q=${encodeURIComponent(query)}&limit=50`);
        setMatResults(res.data?.materiales || []);
      } catch { setMatResults([]); }
      setMatSearching(false);
    }, 300);
  };

  const selectProvFromSearch = async (prov) => {
    setManualProv(p => ({
      ...p,
      nombre: prov.nombre || '',
      rut: prov.rut || '',
      email: prov.email || '',
    }));
    setProvSearch('');
    setProvResults([]);
    // Iter 365 — condición desde Alegra SOLO vía su equivalente vigente del catálogo ERP
    const rut = prov.rut || '';
    if (rut) {
      try {
        const res = await api.get(`/compras/condiciones-pago-proveedor/${encodeURIComponent(rut)}`);
        const { alegra_term, sugerida, sin_equivalente } = res.data || {};
        if (sugerida?.nombre) {
          setManualProv(p => ({ ...p, condicion_pago: sugerida.nombre }));
        } else if (alegra_term && sin_equivalente) {
          toast.warning(
            `Alegra indica "${alegra_term.nombre}" para ${prov.nombre || 'este proveedor'} y el ERP no tiene una condición equivalente. ` +
            'Créala en Configuración → Condiciones de Pago o elige una vigente.',
            { duration: 9000 },
          );
        }
      } catch { /* ignore */ }
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/compras/cotizaciones/${id}/comparativo`);
      setData(res.data);
    } catch (err) {
      toast.error('Error al cargar comparativo');

    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Iter 286.d — cargar las 13 condiciones vigentes al montar
  useEffect(() => {
    api.get('/condiciones-pago-crud', { params: { solo_nuevas: true } })
      .then((r) => setCondicionesVigentes(r.data || []))
      .catch(() => setCondicionesVigentes([]));
    // Iter 374 — medios de pago (para Contado / Tarjeta al generar OCs)
    api.get('/medios-pago')
      .then((r) => setMediosPago((r.data || []).filter((m) => m.habilitado_erp !== false)))
      .catch(() => setMediosPago([]));
  }, []);

  // Iter 374 — datos de pago por proveedor seleccionado (prefill desde la SC / maestro)
  useEffect(() => {
    if (!data || !condicionesVigentes.length) return;
    setPagos((prev) => {
      const next = { ...prev };
      new Set(Object.values(selected).filter((v) => v !== undefined)).forEach((pi) => {
        if (!next[pi]) next[pi] = { condicion_pago_codigo: resolverCondicionInicial(data.proveedores[pi], condicionesVigentes), medio_pago_codigo: '' };
      });
      return next;
    });
  }, [data, selected, condicionesVigentes]);

  const handleToggle = (itemIdx, provIdx) => {
    setSelected(prev => {
      const key = `${itemIdx}`;
      return { ...prev, [key]: prev[key] === provIdx ? undefined : provIdx };
    });
  };

  const selectCheapest = () => {
    if (!data) return;
    const sel = {};
    data.items.forEach(item => {
      // Iter 297: no seleccionar ítems que ya tienen OC asignada
      if (item.oc_numero) return;
      if (item.min_prov_idx !== null && item.min_prov_idx !== undefined) {
        sel[`${item.index}`] = item.min_prov_idx;
      }
    });
    setSelected(sel);
    toast.success('Seleccionados los precios mas bajos');
  };

  const handleImprimir = async () => {
    setPrinting(true);
    try {
      const res = await api.get(`/compras/cotizaciones/${id}/comparativo/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Error al generar el PDF del comparativo');
    }
    setPrinting(false);
  };

  // Group selections by provider
  const selectionSummary = useMemo(() => {
    if (!data) return [];
    const groups = {};
    Object.entries(selected).forEach(([itemKey, provIdx]) => {
      if (provIdx === undefined) return;
      const itemIdx = parseInt(itemKey);
      const item = data.items[itemIdx];
      const prov = data.proveedores[provIdx];
      if (!item || !prov) return;
      if (!groups[provIdx]) {
        groups[provIdx] = { prov, provIdx: Number(provIdx), items: [], neto: 0 };
      }
      const precio = item.precios[provIdx];
      groups[provIdx].items.push({ ...item, precio });
      groups[provIdx].neto += precio?.total || 0;
    });
    return Object.values(groups);
  }, [data, selected]);

  const handleGenerar = async () => {
    const selecciones = Object.entries(selected)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => ({ item_index: parseInt(k), proveedor_index: v }));
    if (selecciones.length === 0) {
      toast.error('Seleccione al menos un item');
      return;
    }
    // Iter 374 — sin condición vigente / medio de pago no se generan OCs
    const faltantes = selectionSummary
      .map((g) => ({ g, err: pagoIncompleto(pagos[g.provIdx], condicionesVigentes) }))
      .filter((x) => x.err);
    if (faltantes.length) {
      toast.error('Datos de pago incompletos', { description: faltantes.map((x) => `${x.g.prov.nombre}: ${x.err}`).join(' · ') });
      return;
    }
    const pagosPayload = selectionSummary.map((g) => ({ proveedor_index: g.provIdx, ...pagos[g.provIdx] }));
    setGenerando(true);
    try {
      const res = await api.post(`/compras/cotizaciones/${id}/generar-ocs`, { selecciones, pagos: pagosPayload });
      // Iter 297: el backend informa si el comparativo se cerró (todos los
      // ítems con OC) o si quedan pendientes → se consulta al usuario.
      setOcsCreadas({
        ocs: res.data?.ocs || [],
        cerrada: !!res.data?.cerrada,
        itemsPendientes: res.data?.items_pendientes || [],
      });
      toast.success(`${res.data?.ocs?.length || 0} OC(s) generada(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar OCs');
    }
    setGenerando(false);
  };

  // Iter 297: cierre manual del comparativo con ítems pendientes
  const handleCerrarComparativo = async () => {
    setCerrando(true);
    try {
      await api.post(`/compras/cotizaciones/${id}/cerrar-comparativo`, { motivo: '' });
      setOcsCreadas(prev => ({ ...prev, cerrada: true, cerradaManual: true }));
      toast.success('Cuadro comparativo cerrado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cerrar el comparativo');
    }
    setCerrando(false);
  };

  // Iter 297: mantener abierto → volver al comparativo mostrando pendientes
  const handleMantenerAbierto = async () => {
    setOcsCreadas(null);
    setSelected({});
    await fetchData();
    toast.info('El cuadro comparativo sigue abierto con los ítems pendientes');
  };

  // Manual provider handlers
  const handleSaveManualProv = async () => {
    if (!manualProv.nombre.trim()) {
      toast.error('Ingrese el nombre del proveedor');
      return;
    }
    const preciosList = Object.entries(manualPrecios)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ item_index: parseInt(k), precio_unitario: parseFloat(v) }));

    if (preciosList.length === 0) {
      toast.error('Ingrese al menos un precio');
      return;
    }

    setSavingManual(true);
    try {
      const payload = {
        ...manualProv,
        precios: preciosList,
        // Iter 313.g — TC (si moneda != CLP)
        tipo_cambio: manualProv.moneda !== 'CLP' ? manualTC : null,
        tipo_cambio_fecha: manualProv.moneda !== 'CLP' ? manualTCFecha : null,
      };
      await api.post(`/compras/cotizaciones/${id}/proveedor-manual`, payload);
      toast.success(`Proveedor ${manualProv.nombre} agregado`);
      setShowAddProv(false);
      setManualProv({ nombre: '', rut: '', email: '', condicion_pago: '', plazo_entrega: '', moneda: 'CLP' });
      setManualPrecios({});
      setManualTC(null); setManualTCFecha('');
      await fetchData();
    } catch (_err) {
      toast.error(_err.response?.data?.detail || 'Error al agregar proveedor');
    }
    setSavingManual(false);
  };

  // Iter 313.g — auto-carga TC cuando cambia la moneda del proveedor manual
  useEffect(() => {
    const m = manualProv.moneda;
    if (!m || m === 'CLP') {
      setManualTC(null); setManualTCFecha('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const hoy = new Date().toISOString().slice(0, 10);
        const r = await api.get(`/finanzas/tipo-cambio?fecha=${hoy}&moneda=${m}`);
        if (!cancelled) {
          setManualTC(r.data.valor);
          setManualTCFecha(r.data.fecha_efectiva || r.data.fecha);
        }
      } catch (err) {
        if (!cancelled) {
          setManualTC(null);
          toast.error(`TC ${m} no disponible: ${err.response?.data?.detail || err.message}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [manualProv.moneda]);

  // Additional item handlers
  const selectMaterial = (mat) => {
    setAddItem(p => ({
      ...p,
      descripcion: mat.descripcion2 || mat.descripcion || '',
      codigo: mat.codigo || '',
      unidad: mat.unidad || 'UN',
    }));
    setMatSearch('');
    setMatResults([]);
  };

  const resetAddItem = () => {
    setAddItem({ descripcion: '', cantidad: '1', unidad: 'UN', codigo: '', observacion: '' });
    setAddItemPrecios({});
    setMatSearch('');
    setMatResults([]);
  };

  const handleSaveItem = async () => {
    if (!addItem.descripcion.trim()) {
      toast.error('Ingrese la descripción del item');
      return;
    }
    const precios = Object.entries(addItemPrecios)
      .filter(([, v]) => parseFloat(v) > 0)
      .map(([k, v]) => ({ proveedor_index: parseInt(k), precio_unitario: parseFloat(v) }));
    setSavingItem(true);
    try {
      await api.post(`/compras/cotizaciones/${id}/item-adicional`, {
        descripcion: addItem.descripcion.trim(),
        cantidad: parseFloat(addItem.cantidad) || 1,
        unidad: addItem.unidad || 'UN',
        codigo: addItem.codigo || '',
        observacion: addItem.observacion || '',
        precios,
      });
      toast.success('Item agregado al comparativo');
      setShowAddItem(false);
      resetAddItem();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar item');
    }
    setSavingItem(false);
  };

  // Inline cell price edit (items adicionales)
  const startEditCell = (itemIdx, provIdx, currentPrice) => {
    setEditCell({ itemIdx, provIdx });
    setEditValue(currentPrice > 0 ? String(currentPrice) : '');
  };

  const saveCellPrice = async (opts = {}) => {
    if (!editCell) return;
    const v = parseFloat(editValue);
    const noCotiza = opts.noCotiza === true || v === 0;
    if (!noCotiza && !(v > 0)) { setEditCell(null); return; }
    setSavingCell(true);
    try {
      const r = await api.post(`/compras/cotizaciones/${id}/precio-item`, {
        proveedor_index: editCell.provIdx,
        item_index: editCell.itemIdx,
        precio_unitario: noCotiza ? 0 : v,
        no_cotiza: noCotiza,
      });
      toast.success(r.data?.message || 'Precio guardado');
      setEditCell(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar precio');
    }
    setSavingCell(false);
  };

  // Iter 361: quitar precio / "No cotiza" → la celda vuelve a "+ precio"
  const clearCellPrice = async () => {
    if (!editCell) return;
    setSavingCell(true);
    try {
      await api.delete(`/compras/cotizaciones/${id}/precio-item`, { params: { proveedor_index: editCell.provIdx, item_index: editCell.itemIdx } });
      toast.success('Precio quitado');
      setEditCell(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al quitar precio');
    }
    setSavingCell(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="comparativo-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No se encontro la solicitud</p>
        <Button variant="outline" onClick={() => navigate('/compras/cotizaciones')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
      </div>
    );
  }

  if (ocsCreadas) {
    const pendientes = ocsCreadas.itemsPendientes || [];
    return (
      <div className="max-w-2xl mx-auto py-8 text-center space-y-4" data-testid="ocs-creadas">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-lg font-bold">Ordenes de Compra Generadas</h2>
        <div className="space-y-2">
          {(ocsCreadas.ocs || []).map((oc, i) => (
            <div key={oc.numero_oc || i} className="bg-white border rounded-lg p-3 flex justify-between items-center">
              <span className="font-mono font-bold text-blue-700">{oc.numero_oc}</span>
              <span className="text-sm text-gray-600">{oc.proveedor}</span>
              <span className="font-semibold">{formatCLP(oc.total)}</span>
            </div>
          ))}
        </div>

        {/* Iter 297: comparativo cerrado automáticamente (todos los ítems con OC) */}
        {ocsCreadas.cerrada && (
          <div className="bg-gray-100 border rounded-lg p-3 text-sm text-gray-600" data-testid="comparativo-cerrado-msg">
            {ocsCreadas.cerradaManual
              ? 'El cuadro comparativo fue cerrado (quedaron ítems sin OC asignada).'
              : 'Cuadro comparativo cerrado automáticamente: todos los ítems tienen OC asignada.'}
          </div>
        )}

        {/* Iter 297: quedan ítems sin OC → consultar al usuario si cierra */}
        {!ocsCreadas.cerrada && pendientes.length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-left space-y-3" data-testid="pendientes-panel">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-amber-800">
                  Quedan {pendientes.length} ítem(s) sin OC asignada
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  ¿Deseas cerrar el cuadro comparativo de todas formas? Si lo mantienes abierto,
                  la próxima vez se presentarán los ítems que aún no tienen OC.
                </p>
              </div>
            </div>
            <ul className="text-xs text-gray-700 bg-white border rounded-md divide-y max-h-40 overflow-y-auto">
              {pendientes.map((it) => (
                <li key={`pend-${it.index}`} className="px-3 py-1.5 flex justify-between" data-testid={`item-pendiente-${it.index}`}>
                  <span>{it.descripcion}</span>
                  <span className="text-gray-400 shrink-0 ml-2">{it.cantidad} {it.unidad}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={handleMantenerAbierto} disabled={cerrando}
                data-testid="btn-mantener-abierto">
                Mantener abierto
              </Button>
              <Button size="sm" onClick={handleCerrarComparativo} disabled={cerrando}
                className="bg-amber-600 hover:bg-amber-700" data-testid="btn-cerrar-comparativo">
                {cerrando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />}
                Cerrar comparativo
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-center mt-4">
          <Button onClick={() => navigate('/compras/ordenes-compra')} className="bg-[#003366]">
            <ShoppingCart className="w-4 h-4 mr-1" /> Ver Ordenes de Compra
          </Button>
          <Button variant="outline" onClick={() => navigate('/compras/cotizaciones')}>
            Volver a Cotizaciones
          </Button>
        </div>
      </div>
    );
  }

  const { items, proveedores } = data;

  return (
    <div className="space-y-4" data-testid="comparativo-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/compras/cotizaciones')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Cuadro Comparativo — {data.numero}
            </h1>
            <p className="text-xs text-gray-500">
              {proveedores.length} proveedor(es) · {data.proveedores_respondidos} respondido(s) · {data.total_items} items
              {data.items_con_oc > 0 && (
                <span className="ml-1 text-amber-700 font-semibold" data-testid="contador-pendientes">
                  · {data.items_con_oc} con OC · {data.items_pendientes} pendiente(s)
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleImprimir} disabled={printing}
            data-testid="btn-imprimir-comparativo">
            {printing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
            Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowAddItem(v => !v); setShowAddProv(false); }}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="btn-add-item">
            <PackagePlus className="w-4 h-4 mr-1" /> Agregar Item
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowAddProv(true); setShowAddItem(false); }}
            data-testid="btn-add-prov">
            <UserPlus className="w-4 h-4 mr-1" /> Agregar Proveedor
          </Button>
          <Button variant="outline" size="sm" onClick={selectCheapest} data-testid="btn-cheapest">
            <Check className="w-4 h-4 mr-1" /> Seleccionar mas baratos
          </Button>
        </div>
      </div>

      {/* Additional Item Form */}
      {showAddItem && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="add-item-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-emerald-800">
              <PackagePlus className="w-4 h-4 inline mr-1" /> Agregar Item al Comparativo
            </h3>
            <Button variant="ghost" size="sm" onClick={() => { setShowAddItem(false); resetAddItem(); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-emerald-700">
            Agregue opciones alternativas ofrecidas por los proveedores o items desde el maestro de materiales.
          </p>

          {/* Material search */}
          <div className="relative">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input placeholder="Buscar en maestro de materiales (opcional)..."
                value={matSearch}
                onChange={e => searchMateriales(e.target.value)}
                className="pl-8 text-sm bg-white"
                data-testid="add-item-mat-search" />
              {matSearching && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-emerald-500" />}
            </div>
            {matResults.length > 0 && (
              <div className="absolute z-50 w-full mt-0.5 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {matResults.map((m, i) => (
                  <button key={`mat-${i}`} onClick={() => selectMaterial(m)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 border-b last:border-0"
                    data-testid={`add-item-mat-result-${i}`}>
                    <span className="font-semibold">{m.descripcion2 || m.descripcion}</span>
                    {m.unidad && <span className="ml-2 text-gray-400">{m.unidad}</span>}
                    {m.familia_nombre && <span className="ml-2 text-emerald-600">{m.familia_nombre}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-12 md:col-span-6">
              <label className="block text-[10px] text-gray-500 mb-0.5">Descripción *</label>
              <Input placeholder="Descripción del material/servicio (texto libre)"
                value={addItem.descripcion}
                onChange={e => setAddItem(p => ({ ...p, descripcion: e.target.value }))}
                className="bg-white text-sm" data-testid="add-item-desc" />
            </div>
            <div className="col-span-4 md:col-span-2">
              <label className="block text-[10px] text-gray-500 mb-0.5">Cantidad</label>
              <NumberInput allowNegative={false} value={addItem.cantidad}
                onChange={clean => setAddItem(p => ({ ...p, cantidad: clean }))}
                className="bg-white text-sm" data-testid="add-item-cant" />
            </div>
            <div className="col-span-3 md:col-span-1">
              <label className="block text-[10px] text-gray-500 mb-0.5">Unidad</label>
              <Input value={addItem.unidad}
                onChange={e => setAddItem(p => ({ ...p, unidad: e.target.value }))}
                className="bg-white text-sm" data-testid="add-item-und" />
            </div>
            <div className="col-span-5 md:col-span-3">
              <label className="block text-[10px] text-gray-500 mb-0.5">Observación / especificación técnica</label>
              <Input placeholder="Ej: AISI304, 3mm..." value={addItem.observacion}
                onChange={e => setAddItem(p => ({ ...p, observacion: e.target.value }))}
                className="bg-white text-sm" data-testid="add-item-obs" />
            </div>
          </div>

          {/* Optional prices per provider */}
          {proveedores.length > 0 && (
            <div className="border rounded-lg overflow-hidden bg-white">
              <div className="px-3 py-1.5 bg-gray-50 text-[11px] text-gray-500 border-b">
                Precio unitario por proveedor (opcional — también puede ingresarlos después en la celda)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
                {proveedores.map((prov, pi) => (
                  <div key={`aip-${pi}`} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 truncate flex-1" title={prov.nombre}>{prov.nombre}</span>
                    <NumberInput allowNegative={false} placeholder="$ P.U."
                      className="w-28 text-right text-sm"
                      value={addItemPrecios[pi] || ''}
                      onChange={clean => setAddItemPrecios(p => ({ ...p, [pi]: clean }))}
                      data-testid={`add-item-precio-${pi}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowAddItem(false); resetAddItem(); }}>Cancelar</Button>
            <Button size="sm" onClick={handleSaveItem} disabled={savingItem}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-item">
              {savingItem ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Agregar Item
            </Button>
          </div>
        </div>
      )}

      {/* Manual Provider Form */}
      {showAddProv && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="manual-prov-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-blue-800">Agregar Proveedor</h3>
            <Button variant="ghost" size="sm" onClick={() => { setShowAddProv(false); setProvSearch(''); setProvResults([]); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Provider search */}
          <div className="relative">
            <Input placeholder="Buscar proveedor por nombre, RUT o email..."
              value={manualProv.nombre ? '' : provSearch}
              onChange={e => searchProviders(e.target.value)}
              disabled={!!manualProv.nombre}
              data-testid="prov-search-comparativo" />
            {provSearching && <Loader2 className="absolute right-2 top-2 w-4 h-4 animate-spin text-blue-500" />}
            {provResults.length > 0 && (
              <div className="absolute z-50 w-full mt-0.5 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {provResults.map((p, i) => (
                  <button key={`pr-${i}`} onClick={() => selectProvFromSearch(p)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b last:border-0">
                    <span className="font-semibold">{p.nombre}</span>
                    {p.rut && <span className="ml-2 text-gray-400">{p.rut}</span>}
                    {p.email && <span className="ml-2 text-blue-500">{p.email}</span>}
                  </button>
                ))}
              </div>
            )}
            {provSearch.length >= 2 && !provSearching && provResults.length === 0 && (
              <div className="absolute z-50 w-full mt-0.5 bg-white border rounded-md shadow-lg p-2">
                <button onClick={() => { setManualProv(p => ({ ...p, nombre: provSearch })); setProvSearch(''); }}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 rounded">
                  <Plus className="w-3 h-3 inline mr-1" />Usar "<strong>{provSearch}</strong>" como proveedor manual
                </button>
              </div>
            )}
          </div>

          {/* Selected or manual provider data */}
          {manualProv.nombre ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-blue-800">{manualProv.nombre}</span>
                {manualProv.rut && <span className="text-xs text-gray-500">{manualProv.rut}</span>}
                {manualProv.email && <span className="text-xs text-blue-500">{manualProv.email}</span>}
                <Button variant="ghost" size="sm" onClick={() => setManualProv({ nombre: '', rut: '', email: '', condicion_pago: '', plazo_entrega: '' })}>
                  <X className="w-3 h-3" /> Cambiar
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Condicion de Pago</label>
                  <select
                    value={manualProv.condicion_pago}
                    onChange={e => setManualProv(p => ({ ...p, condicion_pago: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-input text-sm bg-white"
                    data-testid="select-cotiz-condicion-pago"
                  >
                    <option value="">Seleccione...</option>
                    {condicionesVigentes.map((c) => (
                      <option key={c.codigo} value={c.nombre}>
                        {c.nombre}{c.dias_plazo ? ` (${c.dias_plazo} días)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Plazo de Entrega</label>
                  <Input placeholder="Plazo entrega" value={manualProv.plazo_entrega}
                    onChange={e => setManualProv(p => ({ ...p, plazo_entrega: e.target.value }))} />
                </div>
              </div>
              {/* Iter 313.g — Selector de moneda del proveedor manual */}
              <div className="mt-2 p-2 bg-gray-50 border rounded-lg" data-testid="prov-manual-moneda">
                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                  <label className="text-[10px] text-gray-500 font-semibold uppercase">Moneda del proveedor:</label>
                  {['CLP','USD','EUR','UF','UTM'].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setManualProv(p => ({ ...p, moneda: m }))}
                      className={`text-[10px] px-2 h-6 rounded border transition-colors ${
                        manualProv.moneda === m
                          ? 'bg-emerald-600 text-white border-emerald-700'
                          : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                      }`}
                      data-testid={`prov-manual-btn-moneda-${m}`}
                    >{m}</button>
                  ))}
                  {manualProv.moneda !== 'CLP' && (
                    <span className="text-[10px] text-gray-600 ml-2">
                      {manualTC
                        ? <>TC {manualProv.moneda}→CLP: <strong className="text-emerald-700 font-mono">${manualTC.toLocaleString('es-CL')}</strong> <span className="text-gray-400">({manualTCFecha})</span></>
                        : <span className="text-red-600">Cargando/TC no disponible</span>}
                    </span>
                  )}
                </div>
                {manualProv.moneda !== 'CLP' && manualTC && (
                  <div className="text-[10px] text-amber-700">
                    ⓘ Precios cotizados en {manualProv.moneda} — el comparativo los normaliza a CLP referencial al TC del día.
                  </div>
                )}
              </div>
              <div className="border rounded-lg overflow-hidden mt-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left text-gray-600 font-semibold">Material</th>
                      <th className="px-3 py-2 text-right text-gray-600 font-semibold w-32">
                        Precio Unitario ({manualProv.moneda})
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={`mp-${item.index}`} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700">
                          {item.descripcion}
                          {item.observacion && (
                            <div className="text-[10px] text-gray-500 italic" data-testid={`manual-obs-${item.index}`}>{item.observacion}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <NumberInput allowNegative={false} placeholder="$ P.U."
                            className="w-full text-right text-sm"
                            value={manualPrecios[item.index] || ''}
                            onChange={clean => setManualPrecios(p => ({ ...p, [item.index]: clean }))}
                            data-testid={`manual-precio-${item.index}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => { setShowAddProv(false); setManualProv({ nombre: '', rut: '', email: '', condicion_pago: '', plazo_entrega: '' }); setManualPrecios({}); }}>Cancelar</Button>
                <Button size="sm" onClick={handleSaveManualProv} disabled={savingManual}
                  className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-manual">
                  {savingManual ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Guardar Proveedor
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-blue-600">Busque un proveedor del listado o escriba un nombre para agregar manualmente.</p>
          )}
        </div>
      )}

      {/* Comparison Table */}
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm" data-testid="comparison-table">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-gray-600 min-w-[200px]">Material</th>
              <th className="px-2 py-2 text-center font-semibold text-gray-600 w-20">Req.</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-600 w-16">Cant</th>
              <th className="px-2 py-2 text-center font-semibold text-gray-600 w-14">Und</th>
              {proveedores.map((prov, pi) => (
                <th key={`ph-${pi}`} className="px-3 py-2 text-center font-semibold text-gray-700 min-w-[140px] border-l">
                  <div className="text-xs leading-tight">{prov.nombre}</div>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    {prov.estado === 'respondida' ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">Respondida</span>
                    ) : prov.manual ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Manual</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">Pendiente</span>
                    )}
                  </div>
                  {(prov.condicion_pago || prov.plazo_entrega) && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {prov.condicion_pago && <span>{prov.condicion_pago}</span>}
                      {prov.plazo_entrega && <span> · {prov.plazo_entrega}</span>}
                    </div>
                  )}
                  {/* Iter 313.g — badge moneda + TC del proveedor */}
                  {prov.moneda && prov.moneda !== 'CLP' && (
                    <div className="text-[10px] text-emerald-700 mt-0.5 flex items-center justify-center gap-1"
                      title={prov.tipo_cambio ? `TC ${prov.moneda}→CLP: $${prov.tipo_cambio.toLocaleString('es-CL')} (referencial)` : ''}
                      data-testid={`prov-moneda-${pi}`}>
                      <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded font-semibold">
                        {prov.moneda}
                      </span>
                      {prov.tipo_cambio && (
                        <span className="text-gray-500 font-mono">
                          TC ${Math.round(prov.tipo_cambio).toLocaleString('es-CL')}
                        </span>
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={`row-${item.index}`}
                className={`border-t ${item.oc_numero ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}
                data-testid={`comp-row-${item.index}`}>
                <td className="px-3 py-2">
                  <div className="text-xs font-medium text-gray-900 flex items-center gap-1.5">
                    {item.descripcion}
                    {item.es_adicional && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full shrink-0"
                        title="Item agregado en el comparativo — precios editables">Adicional</span>
                    )}
                    {item.oc_numero && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full shrink-0 font-mono"
                        title={`OC ya generada${item.oc_proveedor ? ` — ${item.oc_proveedor}` : ''}`}
                        data-testid={`badge-oc-item-${item.index}`}>
                        OC-{item.oc_numero}
                      </span>
                    )}
                  </div>
                  {item.observacion && <div className="text-[10px] text-gray-500 italic">{item.observacion}</div>}
                </td>
                <td className="px-2 py-2 text-center text-xs font-mono text-indigo-700" data-testid={`comp-req-${item.index}`}>
                  {item.requisicion ? (
                    <span title={`Requisición #${item.requisicion}${item.ot_numero ? ` — OT ${item.ot_numero}` : ''}`}>
                      {item.requisicion}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-xs">{item.cantidad}</td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">{item.unidad}</td>
                {item.precios.map((precio, pi) => {
                  const isMin = item.min_prov_idx === pi && precio.cotizado;
                  const isSel = selected[`${item.index}`] === pi;
                  const isEditing = editCell && editCell.itemIdx === item.index && editCell.provIdx === pi;
                  // Iter 286.s (Carlos, feb-2026): permitir edición de precios en TODAS
                  // las celdas (antes solo los items marcados como "Adicional").
                  // Iter 297: excepto ítems que ya tienen OC asignada.
                  const editable = !item.oc_numero;
                  return (
                    <td key={`cell-${item.index}-${pi}`}
                      className={`px-3 py-2 text-center border-l transition-colors group relative
                        ${isSel ? 'bg-blue-100 ring-2 ring-inset ring-blue-400' : ''}
                        ${isMin && !isSel ? 'bg-green-50' : ''}
                        ${item.oc_numero ? 'cursor-not-allowed' : (!precio.cotizado && !editable ? 'bg-gray-50 text-gray-300' : 'cursor-pointer hover:bg-blue-50')}
                      `}
                      onClick={() => {
                        if (item.oc_numero) return;
                        if (isEditing) return;
                        if (precio.cotizado) { handleToggle(item.index, pi); }
                        else if (editable) { startEditCell(item.index, pi, 0); }
                      }}
                      data-testid={`cell-${item.index}-${pi}`}
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <NumberInput allowNegative={false} autoFocus
                            className="w-28 h-7 text-right text-xs"
                            value={editValue}
                            placeholder="0 = no cotiza"
                            onChange={clean => setEditValue(clean)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveCellPrice();
                              if (e.key === 'Escape') setEditCell(null);
                            }}
                            data-testid={`edit-precio-${item.index}-${pi}`} />
                          <button onClick={() => saveCellPrice()} disabled={savingCell}
                            className="text-emerald-600 hover:text-emerald-800"
                            title="Guardar precio"
                            data-testid={`save-precio-${item.index}-${pi}`}>
                            {savingCell ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button onClick={() => saveCellPrice({ noCotiza: true })} disabled={savingCell}
                            className="text-amber-600 hover:text-amber-800"
                            title="No cotiza: este proveedor no ofrece el ítem (queda fuera de la comparación)"
                            data-testid={`nocotiza-precio-${item.index}-${pi}`}>
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                          {(precio.cotizado || precio.no_cotiza) && (
                            <button onClick={clearCellPrice} disabled={savingCell}
                              className="text-red-500 hover:text-red-700"
                              title="Quitar precio (vuelve a sin precio)"
                              data-testid={`clear-precio-${item.index}-${pi}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => setEditCell(null)} className="text-gray-400 hover:text-gray-600" title="Cancelar">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : precio.no_cotiza ? (
                        <div className="text-[10px] text-gray-400 italic" title="Este proveedor no cotiza el ítem — no se considera en la comparación" data-testid={`nocotiza-${item.index}-${pi}`}>
                          <Ban className="w-3 h-3 inline mr-0.5" /> No cotiza
                          {editable && (
                            <button className="ml-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 align-middle"
                              title="Editar"
                              onClick={e => { e.stopPropagation(); startEditCell(item.index, pi, 0); }}
                              data-testid={`edit-btn-${item.index}-${pi}`}>
                              <Pencil className="w-3 h-3 inline" />
                            </button>
                          )}
                        </div>
                      ) : precio.cotizado ? (
                        <div>
                          <div className={`text-xs font-semibold ${isMin ? 'text-green-700' : ''} ${isSel ? 'text-blue-700' : ''}`}>
                            {precio.moneda && precio.moneda !== 'CLP'
                              ? <><span className="text-[9px] mr-0.5">{precio.moneda}</span>{formatCLP(precio.precio_unitario).replace('$', '')}</>
                              : formatCLP(precio.precio_unitario)}
                            {editable && (
                              <button className="ml-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 align-middle"
                                title="Editar precio"
                                onClick={e => { e.stopPropagation(); startEditCell(item.index, pi, precio.precio_unitario); }}
                                data-testid={`edit-btn-${item.index}-${pi}`}>
                                <Pencil className="w-3 h-3 inline" />
                              </button>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {precio.moneda && precio.moneda !== 'CLP' && precio.total_clp
                              ? <span title={`Total en ${precio.moneda}: ${formatCLP(precio.total).replace('$', '')} · Referencial en CLP con TC del día`}>
                                  ≈ {formatCLP(precio.total_clp)}
                                </span>
                              : formatCLP(precio.total)}
                          </div>
                          {isSel && <Check className="w-3 h-3 text-blue-600 mx-auto mt-0.5" />}
                        </div>
                      ) : editable ? (
                        <span className="text-[10px] text-emerald-600 opacity-60 group-hover:opacity-100">
                          <Plus className="w-3 h-3 inline" /> precio
                        </span>
                      ) : (
                        <span className="text-[10px]">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Selection Summary */}
      {selectionSummary.length > 0 && (
        <div className="bg-white rounded-lg border p-4 space-y-3" data-testid="selection-summary">
          <h3 className="font-semibold text-sm text-gray-700">Resumen de Seleccion — OCs a Generar</h3>
          {selectionSummary.map((g, gi) => (
            <div key={`sum-${gi}`} className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-sm">{g.prov.nombre}</span>
                <div className="text-right">
                  <div className="text-xs text-gray-500">{g.items.length} item(s)</div>
                  <div className="font-bold text-sm">Neto: {formatCLP(g.neto)} · IVA: {formatCLP(g.neto * 0.19)} · Total: {formatCLP(g.neto * 1.19)}</div>
                </div>
              </div>
              <PagoProveedorSelector
                provIdx={g.provIdx}
                pago={pagos[g.provIdx]}
                vigentes={condicionesVigentes}
                medios={mediosPago}
                onChange={(p) => setPagos((prev) => ({ ...prev, [g.provIdx]: p }))}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <Button onClick={handleGenerar} disabled={generando || selectionSummary.some((g) => pagoIncompleto(pagos[g.provIdx], condicionesVigentes))}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-generar-ocs">
              {generando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShoppingCart className="w-4 h-4 mr-1" />}
              Generar {selectionSummary.length} OC(s)
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
